import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatValue } from "@/lib/post_analysis_utils/formatValue";
import TableSkeleton from "../ui/TableSkeleton";

const UnifiedTable = ({
  columns,
  data,
  isLoading,
  page,
  setPage,
  pageSize = 20,
  setPageSize,
  totalPages,
  title,
  onExportCSV,
  idKey = "uniqueId"
}) => {
  const formatCellValue = (value, type, header) => {
    if (value === null || value === undefined) return '';
    if (
      header === "ID" ||
      ["Pos Con Count", "Neg Con Count", "Pos Test Count", "Neg Test Count"].includes(header) ||
      ["Campaign Name", "Region", "Brand", "Uplift Sign"].includes(header)
    ) {
      return value ?? "";
    }
    if (type === 'percent' || type === 'currency' || typeof value === 'number') {
      return formatValue(value, { type, header });
    }
    return String(value);
  };

  const getTableContainerStyle = () => {
    if (pageSize <= 20) return {};
    return { maxHeight: '840px', overflowY: 'auto' };
  };

  return (
    <div className="bg-background text-foreground border border-border rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        {data.length > 0 && (
          <button
            className="px-3 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 text-sm"
            onClick={onExportCSV}
          >
            Export CSV
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <div style={getTableContainerStyle()}>
          {isLoading ? (
            <TableSkeleton />
          ) : data.length > 0 ? (
            <Table>
              <TableHeader className={pageSize > 20 ? "sticky top-0 bg-background z-10" : ""}>
                <TableRow>
                  {columns.map(col => (
                    <TableHead key={col.accessor}>{col.header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row, i) => (
                  <TableRow 
                    key={row[idKey] || i} 
                    className={(i % 2 === 0 ? 'bg-background' : 'bg-muted') + ' text-foreground'}
                  >
                    {columns.map(col => {
                      const value = row[col.accessor];
                      // Custom rendering for POC column with name and ID
                      if (col.header === 'POC' && col.showPocId) {
                        const pocName = value || '';
                        const pocId = row['poc_id'] || '';
                        // If poc_name already contains the ID, just use it; otherwise append ID
                        const displayName = pocName && pocName.includes(pocId) 
                          ? pocName 
                          : pocName && pocId 
                            ? `${pocName} - ${pocId}`
                            : pocName || pocId || '';
                        return (
                          <TableCell key={col.accessor} className="text-foreground">
                            <span>{displayName}</span>
                          </TableCell>
                        );
                      }
                      // Custom rendering for POC Name column - show "Default POC {poc_id}" if empty
                      if (col.header === 'POC Name' && col.type === 'text') {
                        const pocName = value || '';
                        const pocId = row['poc_id'] || '';
                        const displayName = pocName ? pocName : (pocId ? `Default POC ${pocId}` : 'Default POC');
                        return (
                          <TableCell key={col.accessor} className="text-foreground">
                            <span className={!pocName ? 'text-muted-foreground italic' : ''}>{displayName}</span>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={col.accessor} className="text-foreground">
                          {formatCellValue(value, col.type, col.header)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-muted-foreground text-center py-4">
              No data available.
            </div>
          )}
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div>
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              className="px-2 py-1 rounded border text-sm disabled:opacity-50"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <button
              className="px-2 py-1 rounded border text-sm disabled:opacity-50"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
          <div>
            Rows per page:
            <select
              className="ml-2 border rounded px-1 py-0.5 text-sm"
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedTable; 